import { inject, Injectable } from "@angular/core";
import moment from "moment";
import { timer } from "rxjs";
import { exhaustMap, first, map, mergeMap, switchMap } from "rxjs/operators";

import { AWSHttpClient } from "@intocare/aws";

interface DownloadFileSyncOptions {
	date?: string;
	includeDate?: boolean;
	path: string;
	params?: {
		[key: string]: string;
	};
}

@Injectable({
	providedIn: "root",
})
export class DownloadModel {
	private readonly http = inject(AWSHttpClient);

	/**
	 * Download a file synchronously.
	 *
	 * @param url - The url to the file.
	 * @param type - An optional file type.
	 */
	downloadFileSync(url: string, type?: string) {
		return this.getFileURL(url, type);
	}

	/**
	 * Download a file asynchronously.
	 *
	 * @param path - The path to the document.
	 * @param date - An optional date of the document.
	 * @param includeDate - Whether we want to include the date in the body.
	 */
	downloadFileAsync(options: DownloadFileSyncOptions) {
		const { path, date, includeDate = true, params: inputParams = {} } = options;

		const params = {
			...inputParams,
		};

		const downloadDate = date ? date : moment().format("YYYY-MM-DD");

		if (includeDate) {
			params.date = downloadDate;
		}

		return this.http.post<{ id: string }>(`/docs/${path}`, params).pipe(
			switchMap((result) => this.pollStatus(result.id)),
			switchMap((result) => this.getFileURL(result.path)),
		);
	}

	/**
	 * Download a file asynchronously with params.
	 *
	 * @param path - The path to the file.
	 * @param params - A params object which we sent along with.
	 */
	downloadFileAsyncWithParams(path: string, params: any) {
		return this.http.post<{ id: string }>(path, params).pipe(
			switchMap((result) => this.pollStatus(result.id)),
			switchMap((result) => this.getFileURL(result.path)),
		);
	}

	/**
	 * Zip and download a list of files asynchronously.
	 *
	 * @param files - A list of file urls.
	 */
	downloadFilesAsync(type: "zip" | "pdf", files: string[]) {
		return this.http.post<{ id: string }>(`docs/${type}`, { files }).pipe(
			switchMap((result) => this.pollStatus(result.id)),
			switchMap((result) => this.getFileURL(result.path)),
		);
	}

	/**
	 * Download a file synchronously by url and type.
	 *
	 * @param path - The url to the file.
	 * @param type - The type of the file.
	 */
	downloadFileWithPathAndType(path: string, type: string) {
		return this.getFileURL(path, type);
	}

	/**
	 * Returns a poller for a specific endpoint.
	 *
	 * @param endpoint - The endpoint of the download job.
	 */
	poller<T = unknown>(endpoint: string) {
		return timer(3000, 3000).pipe(exhaustMap(() => this.http.get<T>(endpoint)));
	}

	/**
	 * Transforms a path url to a signed url so we can download it.
	 *
	 * @param path - The url of the file.
	 * @param type - Optional type of the file.
	 */
	private getFileURL(path: string, type = "document") {
		return this.http
			.post<{ url: string }>(`/download/${type}/signed-url`, { path })
			.pipe(map((response) => response.url));
	}

	/**
	 * Returns the poll status of a asynchronous download request, returns every 3 seconds after 500ms.
	 *
	 * @param id - The Id of the download job.
	 */
	private pollStatus(id: string) {
		// Poll every 3 seconds for the status of the zip starting after 500ms
		return timer(500, 3000).pipe(
			mergeMap(() => this.http.get<{ status: boolean; path?: string }>(`/docs/status/${id}`)),
			first((result) => result.status === true),
		);
	}
}
